import React, { useState } from 'react';
import './contact.css';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import PlaceIcon from '@mui/icons-material/PlaceOutlined';
import SendIcon from '@mui/icons-material/Send';
import emailjs from '@emailjs/browser';
import Reveal from '../Reveal/Reveal';
import SocialLinks from '../SocialLinks/SocialLinks';
import { contact, profile } from '../../data/profile';

const EMAILJS_SERVICE = 'service_aan553g';
const EMAILJS_TEMPLATE = 'template_5yqcswm';
const EMAILJS_PUBLIC_KEY = 'bvVP9wHLcWYDj3mCG';

const EMAIL_PATTERN = /^[\w-.]+@([\w-]+\.)+[\w-]{2,10}$/;

const EMPTY_FORM = { username: '', email: '', message: '' };

function validate({ username, email, message }) {
  const errors = {};
  if (!username.trim()) errors.username = 'Please tell me your name.';
  if (!email.trim()) errors.email = 'Please add an email so I can reply.';
  else if (!EMAIL_PATTERN.test(email.trim())) errors.email = 'That email address looks off.';
  if (!message.trim()) errors.message = 'Please write a short message.';
  return errors;
}

function Contact() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  const update = (field) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      if (!current[field]) return current;
      const { [field]: _removed, ...rest } = current;
      return rest;
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      setStatus('idle');
      return;
    }

    setStatus('sending');
    emailjs
      .send(
        EMAILJS_SERVICE,
        EMAILJS_TEMPLATE,
        {
          username: form.username.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
        },
        EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setForm(EMPTY_FORM);
        setStatus('sent');
      })
      .catch(() => setStatus('error'));
  };

  const details = [
    { icon: <EmailIcon fontSize="small" />, label: 'Email', value: contact.email, href: `mailto:${contact.email}` },
    { icon: <PhoneIcon fontSize="small" />, label: 'Phone', value: contact.phone, href: `tel:${contact.phone}` },
    { icon: <PlaceIcon fontSize="small" />, label: 'Location', value: profile.location },
  ];

  return (
    <section className="section section--alt" id="contact">
      <div className="container">
        <Reveal className="section-head">
          <span className="section-eyebrow">Contact</span>
          <h2 className="section-title">Let&apos;s work together</h2>
          <p className="section-sub">
            Have a data platform to build, an architecture to review, or a role in
            mind? Send a message and I&apos;ll get back to you.
          </p>
        </Reveal>

        <div className="contact-grid">
          <Reveal className="card contact-form-card">
            <form onSubmit={handleSubmit} noValidate>
              <div className="field">
                <label htmlFor="contact-name">Name</label>
                <input
                  id="contact-name"
                  type="text"
                  value={form.username}
                  onChange={update('username')}
                  placeholder="Your name"
                  aria-invalid={Boolean(errors.username)}
                  aria-describedby={errors.username ? 'contact-name-error' : undefined}
                />
                {errors.username && (
                  <p className="field-error" id="contact-name-error">
                    {errors.username}
                  </p>
                )}
              </div>

              <div className="field">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  type="email"
                  value={form.email}
                  onChange={update('email')}
                  placeholder="you@example.com"
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? 'contact-email-error' : undefined}
                />
                {errors.email && (
                  <p className="field-error" id="contact-email-error">
                    {errors.email}
                  </p>
                )}
              </div>

              <div className="field">
                <label htmlFor="contact-message">Message</label>
                <textarea
                  id="contact-message"
                  rows="5"
                  value={form.message}
                  onChange={update('message')}
                  placeholder="What would you like to build?"
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? 'contact-message-error' : undefined}
                />
                {errors.message && (
                  <p className="field-error" id="contact-message-error">
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="form-footer">
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'Sending…' : 'Send message'}
                  <SendIcon fontSize="small" />
                </button>

                <p className="form-status" role="status" aria-live="polite">
                  {status === 'sent' && (
                    <span className="form-status--ok">
                      Thank you — your message is on its way.
                    </span>
                  )}
                  {status === 'error' && (
                    <span className="form-status--error">
                      Something went wrong. Please email me directly at {contact.email}.
                    </span>
                  )}
                </p>
              </div>
            </form>
          </Reveal>

          <Reveal className="contact-details" delay={100}>
            <ul className="contact-detail-list">
              {details.map(({ icon, label, value, href }) => (
                <li key={label} className="card contact-detail">
                  <span className="contact-detail-icon" aria-hidden="true">
                    {icon}
                  </span>
                  <div>
                    <span className="contact-detail-label">{label}</span>
                    {href ? (
                      <a className="contact-detail-value" href={href}>
                        {value}
                      </a>
                    ) : (
                      <span className="contact-detail-value">{value}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            <div className="contact-socials">
              <h3 className="contact-socials-title">Find me on</h3>
              <SocialLinks />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default Contact;
